<template>
  <MusubiSectionCard title="Today's sending allowance" icon="clock" class="sa-card">
    <div v-if="loading" class="text-muted small">Loading...</div>
    <div v-else-if="error" class="text-muted small">{{ error }}</div>
    <template v-else-if="data">
      <p v-if="!data.enforced" class="small text-muted mb-2">
        Daily limits are switched off on this platform at the moment, so nothing is held back by them.
      </p>
      <div class="sa-row">
        <span class="sa-label">This workspace</span>
        <div class="sa-bar" :title="`${data.workspace.used_today} of ${data.workspace.cap} used today`">
          <div class="sa-bar__fill" :style="{ width: barPct(data.workspace.used_today, data.workspace.cap) }"></div>
        </div>
        <span class="small">{{ data.workspace.used_today }} of {{ data.workspace.cap }} used today, {{ data.workspace.remaining_today }} left</span>
      </div>
      <div v-for="d in data.domains" :key="d.id" class="sa-row">
        <span class="sa-label">{{ d.domain }}</span>
        <template v-if="d.warmup_enabled">
          <div class="sa-bar" :title="`${d.used_today} of ${d.today_cap} used today`">
            <div class="sa-bar__fill" :style="{ width: barPct(d.used_today, d.today_cap) }"></div>
          </div>
          <span class="small">
            {{ d.used_today }} of {{ d.today_cap }} used today.
            {{ d.warmup_day ? `Warm-up day ${d.warmup_day}.` : 'Warm-up starts with its first campaign, drip or one-off email.' }}
          </span>
        </template>
        <span v-else class="small text-muted">{{ d.used_today }} sent today. No warm-up limit for this domain.</span>
      </div>
      <p class="small text-muted mb-0">
        <template v-if="deferredTotal">
          {{ deferredTotal }} email{{ deferredTotal === 1 ? '' : 's' }} will go out when the allowance resets
          ({{ data.deferred.daily_cap }} over the daily limit, {{ data.deferred.warmup }} over a domain's warm-up).
        </template>
        The allowance resets at midnight UK time. A new sending domain starts at {{ firstDay }} emails a day and
        grows each day. To change these numbers, please contact us.
      </p>
    </template>
  </MusubiSectionCard>
</template>

<script>
/**
 * SendAllowanceWidget - the workspace's daily sending allowance, read only
 * (send safety slice 8): the workspace's daily cap and what is used today,
 * each sending domain's warm-up day and allowance, and what waits for the
 * next day. Reads /api/v1/admin/sending/allowance (Ops::SETTINGS, like the
 * Deliverability page it sits on). Operators change the numbers from the
 * tenant drawer (SendingStateControl).
 */
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiSectionCard from "@root/components/ui/MusubiSectionCard.vue";

export default {
  name: 'SendAllowanceWidget',
  components: { MusubiSectionCard },
  data() {
    return { api: useApi(), loading: true, error: '', data: null };
  },
  computed: {
    deferredTotal() {
      const d = this.data?.deferred || {};
      return (d.daily_cap || 0) + (d.warmup || 0);
    },
    firstDay() {
      const first = (this.data?.domains || [])[0];
      return first ? first.warmup_start : 20;
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true; this.error = '';
      try {
        const res = await this.api.get('/api/v1/admin/sending/allowance');
        const p = res?.data ?? res;
        this.data = p && p.workspace ? p : (p?.data ?? null);
      } catch (e) {
        this.error = 'The sending allowance could not be loaded.';
      }
      this.loading = false;
    },
    barPct(used, cap) {
      if (!cap) return '100%';
      return Math.min(100, Math.round((used / cap) * 100)) + '%';
    },
  },
};
</script>

<style scoped>
.sa-card { margin-bottom: 18px; }
.sa-row { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 8px; }
.sa-label { min-width: 180px; font-weight: 600; font-size: 0.85rem; }
.sa-bar { flex: 1; min-width: 120px; max-width: 280px; height: 8px; background: var(--m-border, #e0e0e0); border-radius: 999px; overflow: hidden; }
.sa-bar__fill { height: 100%; background: var(--m-accent, #1976d2); }
</style>
